import { SortDirection } from '@app/shared/types/enums/sort-direction.enum';
import { TaskSortField } from '@app/shared/types/enums/task-sort-field';
import { Task } from '@app/shared/types/models/task/task.model';
import { ProffeoUiTableSort } from '@app/shared/ui/types/table.types';
import { tasksShareGroup } from '../task-grouping.util';

/** Flattened task row used by the desktop task table, including tree depth. */
export interface TaskTableRow {
  task: Task;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
  /** Root owning this row for page grouping; children remain under their root. */
  groupingTask?: Task;
}

export const TASK_TABLE_COLUMN_TO_SORT_FIELD: Readonly<Record<string, TaskSortField>> = {
  number: TaskSortField.Type,
  name: TaskSortField.Name,
  status: TaskSortField.Status,
  priority: TaskSortField.Priority,
  progress: TaskSortField.PercentageOfProgress,
  startDate: TaskSortField.StartDate,
  endDate: TaskSortField.EndDate
};

export const TASK_TABLE_SORT_FIELD_TO_COLUMN: Readonly<Partial<Record<TaskSortField, string>>> = {
  [TaskSortField.Type]: 'number',
  [TaskSortField.Name]: 'name',
  [TaskSortField.Status]: 'status',
  [TaskSortField.Priority]: 'priority',
  [TaskSortField.PercentageOfProgress]: 'progress',
  [TaskSortField.StartDate]: 'startDate',
  [TaskSortField.EndDate]: 'endDate',
  [TaskSortField.Id]: 'number',
  [TaskSortField.TaskNumber]: 'number'
};

export type TaskTableSortChange = { orderBy: TaskSortField; order: SortDirection } | 'clear';

export function resolveTaskTableSort(orderBy: TaskSortField, order: SortDirection): ProffeoUiTableSort | null {
  const columnId = TASK_TABLE_SORT_FIELD_TO_COLUMN[orderBy];
  if (!columnId) {
    return null;
  }

  return {
    columnId,
    direction: order === SortDirection.ASC ? 'asc' : 'desc'
  };
}

export function mapTaskTableSortChange(sort: ProffeoUiTableSort): TaskTableSortChange | null {
  const orderBy = TASK_TABLE_COLUMN_TO_SORT_FIELD[sort.columnId];
  if (!orderBy) {
    return null;
  }

  if (!sort.direction) {
    return 'clear';
  }

  return {
    orderBy,
    order: sort.direction === 'asc' ? SortDirection.ASC : SortDirection.DESC
  };
}

export function flattenTaskTableRows(
  tasks: readonly Task[],
  expandedTaskIds: ReadonlySet<string>,
  resolveChildren: (task: Task) => readonly Task[]
): TaskTableRow[] {
  const rows: TaskTableRow[] = [];

  const appendTask = (task: Task, depth: number, root: Task): void => {
    const taskId = task.id ?? '';
    const hasChildren = (task.subtasksCount ?? 0) > 0;
    const expanded = hasChildren && expandedTaskIds.has(taskId);
    rows.push({ task, depth, hasChildren, expanded, groupingTask: root });

    if (!expanded) {
      return;
    }

    for (const child of resolveChildren(task)) {
      appendTask(child, depth + 1, root);
    }
  };

  for (const task of tasks) {
    appendTask(task, 0, task);
  }

  return rows;
}

export function taskTableRowsShareStatus(row: TaskTableRow, representative: TaskTableRow): boolean {
  return tasksShareGroup(row.groupingTask ?? row.task, representative.groupingTask ?? representative.task, 'status');
}

export function taskTableRowsSharePriority(row: TaskTableRow, representative: TaskTableRow): boolean {
  return tasksShareGroup(row.groupingTask ?? row.task, representative.groupingTask ?? representative.task, 'priority');
}

export function taskTableRowsShareIndustry(row: TaskTableRow, representative: TaskTableRow): boolean {
  return tasksShareGroup(row.groupingTask ?? row.task, representative.groupingTask ?? representative.task, 'industry');
}

export function taskTableRowsShareLocation(row: TaskTableRow, representative: TaskTableRow): boolean {
  return tasksShareGroup(row.groupingTask ?? row.task, representative.groupingTask ?? representative.task, 'location');
}

export function resolveTaskTableIndustryGroupLabel(row: TaskTableRow): string | null {
  const projectIndustry = readSignalValue(row.task.projectIndustry);
  const industry = readSignalValue(projectIndustry?.industry);
  const name = industry?.name?.trim();
  return name ? name : null;
}

export function resolveTaskTableLocationGroupLabel(row: TaskTableRow): string | null {
  const name = readSignalValue(row.task.location)?.name?.trim();
  return name ? name : null;
}

function readSignalValue<T>(value: (() => T) | T | undefined | null): T | undefined {
  if (typeof value === 'function') {
    return (value as () => T)();
  }

  return value ?? undefined;
}
